import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { ChevronRight, ChevronLeft, Check, AlertCircle } from 'lucide-react'
import { createFunnelConnector, getSourceColumns, saveFunnelConfig, listFunnelConnectors } from '../api/funnel'
import { getIngredients } from '../api/brainer'
import { getDatasets } from '../api/storage'
import type { FunnelConnector, FunnelTable } from '../types'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import { Select, SelectItem } from '../components/ui/select'
import { Combobox } from '../components/ui/combobox'

// ── Connector metadata ─────────────────────────────────────────────────────────

const CONNECTOR_META: Record<string, { name: string; defaultPort: number }> = {
  mysql:      { name: 'MySQL',      defaultPort: 3306 },
  postgresql: { name: 'PostgreSQL', defaultPort: 5432 },
  sftp:       { name: 'SFTP',       defaultPort: 22 },
}

function getConnectorMeta(type: string) {
  return CONNECTOR_META[type] ?? { name: type, defaultPort: 3306 }
}

// ── Internal types ─────────────────────────────────────────────────────────────

type TableType = 'data' | 'relation'

export interface TableConfig {
  tableName: string
  tableType: TableType
  idColumn: string
  cupName: string
  isAutoIncluded: boolean
  columns: ColumnConfig[]
  link1TableName: string
  link1IdColumn: string
  link2TableName: string
  link2IdColumn: string
  label: string
}

export interface ColumnConfig {
  columnName: string
  traitName: string
  isRelation: boolean
  isImported: boolean
  relationTargetTable: string
  relationTargetIdColumn: string
  ingredientId: string
}

// ── Step indicator ─────────────────────────────────────────────────────────────

export function Steps({ current }: { current: number }) {
  const labels = ['Connect', 'Tables', 'Columns']
  return (
    <div className="flex items-center gap-0 mb-6">
      {labels.map((label, i) => {
        const done = i < current
        const active = i === current
        return (
          <div key={i} className="flex items-center">
            {i > 0 && <div className={`h-px w-8 ${done ? 'bg-accent' : 'bg-paper-border'}`} />}
            <div className="flex items-center gap-1.5">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-colors ${
                done ? 'bg-accent text-white' : active ? 'bg-accent text-white' : 'bg-paper-border text-ink-dim'
              }`}>
                {done ? <Check size={10} /> : i + 1}
              </div>
              <span className={`text-sm font-medium ${active ? 'text-accent' : done ? 'text-ink-dim' : 'text-ink-dim'}`}>
                {label}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Step 1: Connection ─────────────────────────────────────────────────────────

function StepConnect({ connectorType, onCreated }: {
  connectorType: string
  onCreated: (connector: FunnelConnector, tables: string[]) => void
}) {
  const meta = getConnectorMeta(connectorType)
  const isSftp = connectorType === 'sftp'
  const [form, setForm] = useState({
    title: '', host: '', port: String(meta.defaultPort),
    databaseName: '', username: '', password: '', storageDatasetId: '',
    sftpConnectorId: '', sftpRemotePath: '', sftpFileTypeFilter: '',
    sftpAuthType: 'password' as 'password' | 'key', privateKey: '',
  })
  const [error, setError] = useState('')
  const { data: datasets = [] } = useQuery({ queryKey: ['storage-datasets'], queryFn: () => getDatasets().then(r => r.data) })
  const { data: connectors = [] } = useQuery({
    queryKey: ['funnel-connectors'], queryFn: () => listFunnelConnectors().then(r => r.data), enabled: !isSftp,
  })
  const sftpConnectors = connectors.filter(c => c.connectorType === 'sftp')

  const mut = useMutation({
    mutationFn: () => createFunnelConnector({
      title: form.title, connectorType, host: form.host, port: Number(form.port), username: form.username,
      ...(isSftp
        ? {
            sftpRemotePath: form.sftpRemotePath || null,
            sftpFileTypeFilter: form.sftpFileTypeFilter || null,
            sftpAuthType: form.sftpAuthType,
            password: form.sftpAuthType === 'password' ? form.password : undefined,
            privateKey: form.sftpAuthType === 'key' ? form.privateKey : undefined,
            storageDatasetId: form.storageDatasetId || null,
          }
        : {
            databaseName: form.databaseName, password: form.password,
            storageDatasetId: form.storageDatasetId || null,
            sftpConnectorId: form.sftpConnectorId || null,
          }),
    }),
    onSuccess: ({ data }) => onCreated(data.connector, data.tables),
    onError: (e: any) => setError(e.response?.data?.error ?? e.message ?? 'Connection failed'),
  })

  const canSubmit = isSftp
    ? !!(form.title && form.host && form.username && form.sftpRemotePath &&
         (form.sftpAuthType === 'password' ? form.password : form.privateKey))
    : !!(form.title && form.host && form.databaseName && form.username && form.password)

  return (
    <div className="space-y-3">
      <Field label="Display name">
        <input
          type="text"
          value={form.title}
          onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
          placeholder={`My ${meta.name} ${isSftp ? 'server' : 'database'}`}
          className="field"
        />
      </Field>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <Field label="Host">
            <input
              type="text"
              value={form.host}
              onChange={e => setForm(p => ({ ...p, host: e.target.value }))}
              placeholder="localhost"
              className="field"
            />
          </Field>
        </div>
        <Field label="Port">
          <input
            type="number"
            value={form.port}
            onChange={e => setForm(p => ({ ...p, port: e.target.value }))}
            placeholder={String(meta.defaultPort)}
            className="field"
          />
        </Field>
      </div>
      {!isSftp && (
        <Field label="Database name">
          <input
            type="text"
            value={form.databaseName}
            onChange={e => setForm(p => ({ ...p, databaseName: e.target.value }))}
            className="field"
          />
        </Field>
      )}
      <Field label="Username">
        <input
          type="text"
          value={form.username}
          onChange={e => setForm(p => ({ ...p, username: e.target.value }))}
          className="field"
        />
      </Field>

      {isSftp ? (
        <>
          <Field label="Authentication">
            <Select
              value={form.sftpAuthType}
              onValueChange={v => setForm(p => ({ ...p, sftpAuthType: v as 'password' | 'key' }))}
            >
              <SelectItem value="password">Password</SelectItem>
              <SelectItem value="key">SSH private key</SelectItem>
            </Select>
          </Field>
          {form.sftpAuthType === 'password' ? (
            <Field label="Password">
              <input
                type="password"
                value={form.password}
                onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                className="field"
              />
            </Field>
          ) : (
            <Field label="Private key">
              <textarea
                value={form.privateKey}
                onChange={e => setForm(p => ({ ...p, privateKey: e.target.value }))}
                placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
                rows={4}
                className="field font-mono text-xs"
              />
            </Field>
          )}
          <Field label="Remote path">
            <input
              type="text"
              value={form.sftpRemotePath}
              onChange={e => setForm(p => ({ ...p, sftpRemotePath: e.target.value }))}
              placeholder="/home/user/data"
              className="field"
            />
          </Field>
          <Field label="File type filter (optional)">
            <input
              type="text"
              value={form.sftpFileTypeFilter}
              onChange={e => setForm(p => ({ ...p, sftpFileTypeFilter: e.target.value }))}
              placeholder="pdf, png, txt"
              className="field"
            />
          </Field>
          <Field label="Destination dataset">
            <Combobox
              value={form.storageDatasetId}
              onValueChange={v => setForm(p => ({ ...p, storageDatasetId: v }))}
              placeholder="Select a dataset..."
              options={datasets.map(d => ({ value: d.id, label: d.title }))}
            />
          </Field>
        </>
      ) : (
        <>
          <Field label="Password">
            <input
              type="password"
              value={form.password}
              onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
              className="field"
            />
          </Field>
          <Field label="Storage dataset (for _document columns)">
            <Combobox
              value={form.storageDatasetId}
              onValueChange={v => setForm(p => ({ ...p, storageDatasetId: v }))}
              placeholder="None — leave unset unless importing document columns"
              options={[
                { value: '', label: 'None — leave unset unless importing document columns' },
                ...datasets.map(d => ({ value: d.id, label: d.title })),
              ]}
            />
          </Field>
          <Field label="SFTP source (for _documentPath columns)">
            <Combobox
              value={form.sftpConnectorId}
              onValueChange={v => setForm(p => ({ ...p, sftpConnectorId: v }))}
              placeholder="None — leave unset unless importing file-path columns"
              options={[
                { value: '', label: 'None — leave unset unless importing file-path columns' },
                ...sftpConnectors.map(c => ({ value: c.id, label: c.title })),
              ]}
            />
          </Field>
        </>
      )}

      {error && (
        <div className="flex items-start gap-2 text-red-600 text-xs bg-red-50 border border-red-100 rounded p-2.5">
          <AlertCircle size={13} className="shrink-0 mt-px" />
          {error}
        </div>
      )}

      <div className="flex justify-end pt-1">
        <Button
          onClick={() => { setError(''); mut.mutate() }}
          disabled={mut.isPending || !canSubmit}
          loading={mut.isPending}
        >
          {mut.isPending ? 'Testing connection...' : 'Connect'}
          <ChevronRight size={14} />
        </Button>
      </div>
    </div>
  )
}

// ── Step 2: Tables ─────────────────────────────────────────────────────────────

export function StepTables({ connector, sourceTables, tables, onChange, onNext, onBack }: {
  connector: FunnelConnector
  sourceTables: string[]
  tables: TableConfig[]
  onChange: (t: TableConfig[]) => void
  onNext: () => void
  onBack: () => void
}) {
  const selectedNames = new Set(tables.map(t => t.tableName))
  const dataTables = tables.filter(t => t.tableType === 'data')

  const toggle = (tableName: string) => {
    if (selectedNames.has(tableName)) {
      onChange(tables.filter(t => t.tableName !== tableName))
    } else {
      onChange([...tables, {
        tableName, tableType: 'data', idColumn: 'id', cupName: '', isAutoIncluded: false,
        columns: [], link1TableName: '', link1IdColumn: '', link2TableName: '', link2IdColumn: '', label: 'belongs_to',
      }])
    }
  }

  const update = (tableName: string, patch: Partial<TableConfig>) =>
    onChange(tables.map(t => t.tableName === tableName ? { ...t, ...patch } : t))

  const blockers = tables.filter(t =>
    t.idColumn.trim() === '' || (t.tableType === 'relation' && (t.link1TableName === '' || t.link2TableName === ''))
  )
  const canNext = tables.length > 0 && blockers.length === 0

  return (
    <div className="space-y-4">
      <p className="text-xs text-ink-dim">
        Found {sourceTables.length} tables in <strong>{connector.databaseName}</strong>. Select which to ingest.
      </p>

      <div className="border border-paper-border rounded-md overflow-hidden divide-y divide-paper-border max-h-96 overflow-y-auto scrollbar-thin">
        {sourceTables.map(name => {
          const cfg = tables.find(t => t.tableName === name)
          return (
            <div key={name} className={`px-3 py-2 ${cfg ? 'bg-accent-soft/40' : 'bg-white'}`}>
              <div className="flex items-center gap-3">
                <input type="checkbox" checked={!!cfg} onChange={() => toggle(name)} className="shrink-0" />
                <span className="font-mono text-sm text-ink flex-1">{name}</span>
                {cfg && (
                  <Select
                    value={cfg.tableType}
                    onValueChange={v => update(name, { tableType: v as TableType })}
                    className="!py-0.5 !text-theme-xs w-28"
                  >
                    <SelectItem value="data">data</SelectItem>
                    <SelectItem value="relation">relation</SelectItem>
                  </Select>
                )}
              </div>

              {cfg && (
                <div className="mt-2 ml-6 grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-[10px] font-medium text-ink-dim mb-0.5">ID column</label>
                    <input value={cfg.idColumn} onChange={e => update(name, { idColumn: e.target.value })} placeholder="id" className="field !text-theme-xs !py-0.5" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-medium text-ink-dim mb-0.5">Cup name (optional)</label>
                    <input value={cfg.cupName} onChange={e => update(name, { cupName: e.target.value })} placeholder={name} className="field !text-theme-xs !py-0.5" />
                  </div>
                  {cfg.tableType === 'relation' && (
                    <>
                      <div className="col-span-2">
                        <label className="block text-[10px] font-medium text-ink-dim mb-0.5">Label</label>
                        <input value={cfg.label} onChange={e => update(name, { label: e.target.value })} placeholder="belongs_to" className="field !text-theme-xs !py-0.5" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-ink-dim mb-0.5">Side 1 table</label>
                        <Select value={cfg.link1TableName} onValueChange={v => update(name, { link1TableName: v })} placeholder="select..." className="!text-theme-xs !py-0.5">
                          {dataTables.filter(t => t.tableName !== name).map(t => <SelectItem key={t.tableName} value={t.tableName}>{t.tableName}</SelectItem>)}
                        </Select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-ink-dim mb-0.5">Side 1 ID column</label>
                        <input value={cfg.link1IdColumn} onChange={e => update(name, { link1IdColumn: e.target.value })} placeholder="id" className="field !text-theme-xs !py-0.5" />
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-ink-dim mb-0.5">Side 2 table</label>
                        <Select value={cfg.link2TableName} onValueChange={v => update(name, { link2TableName: v })} placeholder="select..." className="!text-theme-xs !py-0.5">
                          {dataTables.filter(t => t.tableName !== name).map(t => <SelectItem key={t.tableName} value={t.tableName}>{t.tableName}</SelectItem>)}
                        </Select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-medium text-ink-dim mb-0.5">Side 2 ID column</label>
                        <input value={cfg.link2IdColumn} onChange={e => update(name, { link2IdColumn: e.target.value })} placeholder="id" className="field !text-theme-xs !py-0.5" />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>

      {tables.length === 0 && (
        <p className="text-xs text-ink-dim">Select at least one table to continue.</p>
      )}
      {blockers.length > 0 && (
        <p className="text-xs text-amber-600">
          {blockers.length === 1 ? 'This table needs' : 'These tables need'} an ID column
          {blockers.some(t => t.tableType === 'relation') ? ', or both sides of the relation,' : ''} before continuing:{' '}
          <strong>{blockers.map(t => t.tableName).join(', ')}</strong>
        </p>
      )}

      <div className="flex justify-between pt-1">
        <Button variant="secondary" icon={ChevronLeft} onClick={onBack}>Back</Button>
        <Button onClick={onNext} disabled={!canNext}>Next <ChevronRight size={14} /></Button>
      </div>
    </div>
  )
}

// ── Step 3: Columns ────────────────────────────────────────────────────────────

export function StepColumns({ connector, tables, onChange, onSave, onBack, saving }: {
  connector: FunnelConnector
  tables: TableConfig[]
  onChange: (t: TableConfig[]) => void
  onSave: () => void
  onBack: () => void
  saving: boolean
}) {
  const [loadingCols, setLoadingCols] = useState(false)
  const [colError, setColError] = useState('')
  const dataTables = tables.filter(t => t.tableType === 'data')
  const { data: ingredients = [] } = useQuery({ queryKey: ['b-ingredients'], queryFn: () => getIngredients().then(r => r.data) })

  useEffect(() => {
    const newTables = dataTables.filter(t => t.columns.length === 0)
    if (newTables.length === 0) return
    setLoadingCols(true)
    getSourceColumns(connector.id, newTables.map(t => t.tableName))
      .then(r => {
        onChange(tables.map(t => {
          if (t.tableType !== 'data' || t.columns.length > 0) return t
          const cols = r.data[t.tableName] ?? []
          return {
            ...t,
            columns: cols.map(col => ({
              columnName: col, traitName: col.replace(/\s+/g, '_'), isRelation: false, isImported: true,
              relationTargetTable: '', relationTargetIdColumn: '', ingredientId: '',
            })),
          }
        }))
      })
      .catch(e => setColError(e.response?.data?.error ?? 'Failed to load columns'))
      .finally(() => setLoadingCols(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connector.id])

  const updateCol = (tableName: string, colName: string, patch: Partial<ColumnConfig>) =>
    onChange(tables.map(t => {
      if (t.tableName !== tableName) return t
      return { ...t, columns: t.columns.map(c => c.columnName === colName ? { ...c, ...patch } : c) }
    }))

  if (loadingCols) return <div className="py-8 text-center text-sm text-ink-dim">Loading columns...</div>

  return (
    <div className="space-y-4">
      {colError && (
        <div className="flex items-start gap-2 text-red-600 text-xs bg-red-50 border border-red-100 rounded p-2.5">
          <AlertCircle size={13} className="shrink-0 mt-px" /> {colError}
        </div>
      )}

      {dataTables.length === 0 && (
        <p className="text-sm text-ink-dim py-4 text-center">No data tables to configure.</p>
      )}

      {dataTables.map(table => (
        <div key={table.tableName} className="border border-paper-border rounded-md overflow-hidden">
          <div className="bg-paper-sunken px-3 py-2 border-b border-paper-border">
            <span className="font-mono text-sm font-semibold text-ink">{table.tableName}</span>
            <span className="ml-2 text-theme-xs text-ink-dim">{table.columns.filter(c => c.isImported).length}/{table.columns.length} columns imported — uncheck to skip, mark FK/relation as needed</span>
          </div>
          <div className="divide-y divide-paper-border max-h-64 overflow-y-auto scrollbar-thin">
            {table.columns.map(col => (
              <div key={col.columnName} className={`px-3 py-2 ${!col.isImported ? 'opacity-40' : col.isRelation ? 'bg-amber-50/40' : ''}`}>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={col.isImported}
                    onChange={e => updateCol(table.tableName, col.columnName, { isImported: e.target.checked })}
                    title="Import this column"
                  />
                  <span className={`font-mono text-sm w-36 shrink-0 ${col.isImported ? 'text-ink' : 'text-ink-dim line-through'}`}>{col.columnName}</span>
                  {col.isImported ? (
                    <>
                      <input
                        value={col.traitName}
                        onChange={e => updateCol(table.tableName, col.columnName, { traitName: e.target.value.replace(/\s/g, '_') })}
                        placeholder="trait_name"
                        className="field !text-theme-xs !py-0.5 flex-1 font-mono"
                      />
                      {!col.isRelation && (
                        <Select
                          value={col.ingredientId || '_default'}
                          onValueChange={v => updateCol(table.tableName, col.columnName, { ingredientId: v === '_default' ? '' : v })}
                          className="!text-theme-xs !py-0.5 w-32 shrink-0"
                        >
                          <SelectItem value="_default">Default (_funnel)</SelectItem>
                          {ingredients.map(ing => (
                            <SelectItem key={ing.id} value={ing.id}>{ing.name}</SelectItem>
                          ))}
                        </Select>
                      )}
                      <label className="flex items-center gap-1.5 text-theme-xs text-ink-dim cursor-pointer select-none shrink-0">
                        <input
                          type="checkbox"
                          checked={col.isRelation}
                          onChange={e => updateCol(table.tableName, col.columnName, { isRelation: e.target.checked })}
                        />
                        FK / relation
                      </label>
                    </>
                  ) : (
                    <span className="flex-1" />
                  )}
                </div>
                {col.isImported && col.isRelation && (
                  <div className="mt-1.5 grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-medium text-ink-dim mb-0.5">References table</label>
                      <Select
                        value={col.relationTargetTable}
                        onValueChange={v => updateCol(table.tableName, col.columnName, { relationTargetTable: v })}
                        placeholder="select..."
                        className="!text-theme-xs !py-0.5"
                      >
                        {tables.filter(t => t.tableType === 'data' && t.tableName !== table.tableName).map(t => (
                          <SelectItem key={t.tableName} value={t.tableName}>{t.tableName}</SelectItem>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium text-ink-dim mb-0.5">References ID column</label>
                      <input
                        value={col.relationTargetIdColumn}
                        onChange={e => updateCol(table.tableName, col.columnName, { relationTargetIdColumn: e.target.value })}
                        placeholder="id"
                        className="field !text-theme-xs !py-0.5"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="flex justify-between pt-1">
        <Button variant="secondary" icon={ChevronLeft} onClick={onBack}>Back</Button>
        <Button onClick={onSave} disabled={saving} loading={saving}>
          Save configuration <Check size={14} />
        </Button>
      </div>
    </div>
  )
}

// ── Converter: backend FunnelTable → wizard TableConfig ───────────────────────

export function funnelTableToConfig(t: FunnelTable): TableConfig {
  return {
    tableName: t.tableName,
    tableType: t.tableType,
    // DB column is NOT NULL but nothing stops it being '' (e.g. an older, auto-included, or
    // relation-type table saved before this field was required) — an empty idColumn on an
    // already-configured table silently fails StepTables' canNext .every() check with no
    // indication of which table is at fault, permanently disabling "Next" when re-editing that
    // connector. Defaulting here self-heals it the next time the connector is opened for editing.
    idColumn: t.idColumn || 'id',
    cupName: t.cupName ?? '',
    isAutoIncluded: t.isAutoIncluded,
    columns: (t.columns ?? []).map(c => ({
      columnName: c.columnName,
      traitName: c.traitName ?? c.columnName.replace(/\s+/g, '_'),
      isRelation: c.isRelation,
      isImported: c.isImported ?? true,
      relationTargetTable: c.relationTargetTable ?? '',
      relationTargetIdColumn: c.relationTargetIdColumn ?? '',
      ingredientId: c.ingredientId ?? '',
    })),
    link1TableName: t.links?.find(l => l.position === 1)?.linkedTableName ?? '',
    link1IdColumn:  t.links?.find(l => l.position === 1)?.linkedIdColumn  ?? '',
    link2TableName: t.links?.find(l => l.position === 2)?.linkedTableName ?? '',
    link2IdColumn:  t.links?.find(l => l.position === 2)?.linkedIdColumn  ?? '',
    label: t.label ?? 'belongs_to',
  }
}

// ── Main wizard ────────────────────────────────────────────────────────────────

export function BrainerFunnelWizard() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const connectorType = searchParams.get('type') ?? 'mysql'
  const meta = getConnectorMeta(connectorType)

  const [step, setStep] = useState(0)
  const [connector, setConnector] = useState<FunnelConnector | null>(null)
  const [sourceTables, setSourceTables] = useState<string[]>([])
  const [tables, setTables] = useState<TableConfig[]>([])
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  const handleCreated = (c: FunnelConnector, t: string[]) => {
    if (c.connectorType === 'sftp') {
      // SFTP connectors have no relational schema to map — skip the Tables/Columns steps.
      navigate(`/brainer/funnels/${c.id}`)
      return
    }
    setConnector(c)
    setSourceTables(t)
    setStep(1)
  }

  const handleSave = async () => {
    if (!connector) return
    setSaving(true); setSaveError('')
    try {
      await saveFunnelConfig(connector.id, {
        tables: tables.map(t => ({
          tableName: t.tableName,
          cupName: t.cupName || null,
          tableType: t.tableType,
          idColumn: t.idColumn,
          isAutoIncluded: false,
          ...(t.tableType === 'data' ? {
            columns: t.columns.map(c => ({
              columnName: c.columnName,
              traitName: c.traitName || c.columnName.replace(/\s+/g, '_'),
              isRelation: c.isRelation,
              isImported: c.isImported,
              relationTargetTable: c.isRelation ? c.relationTargetTable || null : null,
              relationTargetIdColumn: c.isRelation ? c.relationTargetIdColumn || null : null,
              ingredientId: c.isRelation ? null : c.ingredientId || null,
            })),
          } : {
            links: [
              { linkedTableName: t.link1TableName, linkedIdColumn: t.link1IdColumn, position: 1 as const },
              { linkedTableName: t.link2TableName, linkedIdColumn: t.link2IdColumn, position: 2 as const },
            ],
            label: t.label || null,
          }),
        })),
      })
      navigate(`/brainer/funnels/${connector.id}`)
    } catch (e: any) {
      setSaveError(e.response?.data?.error ?? 'Save failed')
      setSaving(false)
    }
  }

  return (
    <div className="max-w-2xl">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 mb-5">
        <button onClick={() => navigate('/brainer/funnels')} className="text-sm text-ink-dim hover:text-ink">
          Connectors
        </button>
        <ChevronRight size={13} className="text-ink-dim" />
        <button onClick={() => navigate('/brainer/funnels/catalog')} className="text-sm text-ink-dim hover:text-ink">
          Add connector
        </button>
        <ChevronRight size={13} className="text-ink-dim" />
        <span className="text-sm font-medium text-ink">{meta.name}</span>
      </div>

      <h1 className="text-xl font-semibold text-ink mb-4">Connect to {meta.name}</h1>

      <Steps current={step} />

      {saveError && (
        <div className="flex items-start gap-2 text-red-600 text-xs bg-red-50 border border-red-100 rounded p-2.5 mb-4">
          <AlertCircle size={13} className="shrink-0 mt-px" /> {saveError}
        </div>
      )}

      {step === 0 && (
        <StepConnect connectorType={connectorType} onCreated={handleCreated} />
      )}
      {step === 1 && connector && (
        <StepTables
          connector={connector}
          sourceTables={sourceTables}
          tables={tables}
          onChange={setTables}
          onNext={() => setStep(2)}
          onBack={() => setStep(0)}
        />
      )}
      {step === 2 && connector && (
        <StepColumns
          connector={connector}
          tables={tables}
          onChange={setTables}
          onSave={handleSave}
          onBack={() => setStep(1)}
          saving={saving}
        />
      )}
    </div>
  )
}
